import { useRef, useEffect, useCallback } from "react";

/**
 * Renders a word that scales down if it's wider than its parent container.
 * Uses transform: scale() for GPU-accelerated, reflow-free scaling.
 * Re-measures on mount, text change, font load and window resize.
 */
const FitWord = ({
    children,
    className = "",
    align = "center",
}: {
    children: string;
    className?: string;
    /** Where the scale-down anchors AND how the text sits in its box.
     *  "center" (default) keeps existing scan-command behaviour; "left"
     *  pins the word to the left edge so it doesn't drift inward as it
     *  shrinks. */
    align?: "left" | "center";
}) => {
    const ref = useRef<HTMLSpanElement>(null);

    const fit = useCallback(() => {
        const el = ref.current;
        if (!el) return;

        // Reset scale and any forced width to measure the natural layout.
        el.style.transform = "";
        el.style.width = "";

        // Available width is the NARROWEST of the boxes around the word:
        //  - the span's own box: in a flex row it already excludes any
        //    sibling (e.g. the location pin) eating into the space;
        //  - every ancestor's content box: in a centred flex COLUMN
        //    (every .scan-command) the span, its parent and the whole
        //    scan-centre block are shrink-wrapped to the text, so none
        //    of them reports an overflow — "LOKALIZACJĘ" in Polish ran
        //    straight off a 360px screen while the English "LOCATION"
        //    happened to fit. The panel (ultimately the viewport) is the
        //    real limit, so the walk goes all the way up.
        const own = el.clientWidth;
        let availableWidth = own > 0 ? own : Infinity;
        for (let a = el.parentElement; a && a !== document.documentElement; a = a.parentElement) {
            const w = a.clientWidth;
            if (!w) continue;
            const acs = window.getComputedStyle(a);
            const content = w - (parseFloat(acs.paddingLeft) || 0) - (parseFloat(acs.paddingRight) || 0);
            if (content > 0 && content < availableWidth) availableWidth = content;
        }
        if (!Number.isFinite(availableWidth)) return;
        const textWidth = el.scrollWidth;

        if (textWidth > availableWidth && availableWidth > 0) {
            // Pin the layout box to the available width so the oversized
            // text no longer widens the page (the scale is visual only),
            // then scale the glyphs down to fit inside it. Overflowing
            // inline text is laid out from the LEFT edge whatever
            // text-align says, so scale from the left and slide the
            // scaled word into the middle ourselves.
            el.style.width = `${availableWidth}px`;
            const scale = (availableWidth / textWidth) * 0.95; // 5% margin
            const dx = align === "center" ? (availableWidth - textWidth * scale) / 2 : 0;
            el.style.transformOrigin = "left center";
            el.style.transform = `translateX(${dx}px) scale(${scale})`;
        } else {
            el.style.transformOrigin = align === "left" ? "left center" : "center";
        }
    }, [align]);

    useEffect(() => {
        // Fit on mount + after a frame (layout may not be ready immediately)
        fit();
        const raf = requestAnimationFrame(fit);
        // Web fonts can land after mount and change the measured width.
        let cancelled = false;
        if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => { if (!cancelled) fit(); }).catch(() => {});
        }

        // Re-fit on resize
        window.addEventListener("resize", fit);
        return () => {
            cancelled = true;
            window.removeEventListener("resize", fit);
            cancelAnimationFrame(raf);
        };
    }, [children, fit]);

    return (
        <span
            ref={ref}
            className={`scan-command-word ${className}`}
            style={{
                transformOrigin: align === "left" ? "left center" : "center",
                textAlign: align,
                display: "block",
            }}
        >
            {children}
        </span>
    );
};

export default FitWord;
